/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useMemo } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import Nav from "./features/nav/Nav";
import Login from "./features/auth/Login";

import Archive from "./features/office-archive/Archive";

import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import { techwiseThemeDark, techwiseThemeLight } from "./utils/Theme";

import { login, selectIsAuthenticated, selectRoles, selectToken } from "./features/auth/authSlice";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";

import { loginHandler } from "./features/auth/authFunctions";
import About from "./features/about/About";
import CompaniesList from "./features/office-companies/CompaniesList";
import WorkSitesList from "./features/office-work-sites/WorkSitesList";
import WorkOrdersList from "./features/office-work-orders/WorkOrdersList";
import Home from "./features/home/Home";
import MaterialsDatabase from "./features/materials-manager/MaterialsDatabase";
import AssetsManager from "./features/assets-manager/AssetsManager";
import { Nullable } from "./utils/Interfaces";
import AssignmentsManager from "./features/office-assignments/AssignmentsManager";
import TechnicianDashboard from "./features/tech-dashboard/TechnicianDashboard";
import TechnicianWorkOrder from "./features/tech-work-order/TechnicianWorkOrder";
import OfficeWorkOrder from "./features/office-work-order/OfficeWorkOrder";
import WorkSiteDetails from "./features/office-work-site/WorkSiteDetails";
import CompanyDetails from "./features/office-company/CompanyDetails";
import ChangePassword from "./features/reset-password/ChangePassword";
import StandaloneNav from "./features/nav/StandaloneNav";
import { updateSites } from "./features/office-work-sites/workSitesFunctions";
import { updateWorkOrders } from "./features/office-work-orders/workOrdersFunctions";
import { updateCompanies } from "./features/office-companies/companiesFunctions";
import BorderlessNav from "./features/nav/BorderlessNav";
import UserManagement from "./features/admin/user-management/UserManagement";
import GlobalSettings from "./features/admin/global-settings/GlobalSettings";
import PageUnderConstruction from "./components/general/PageUnderConstruction";
import StyledBreadcrumbs from "./components/general/StyledBreadcrumbs";
import TechwiseOfficesList from "./features/materials-techwise-offices/TechwiseOfficesList";
import ValidateRoles from "./features/nav/ValidateRoles";
import { updateAssets, updateAssetsCatalog, updateAvailableAssets } from "./features/assets-manager/assetsFunctions";
import AssetsAssignment from "./features/assets-manager/AssetsAssignment";
import { updateTechwiseOffices } from "./features/materials-techwise-offices/techwiseOfficesFunctions";
import TechwiseOfficeDetails from "./features/materials-techwise-office/TechwiseOfficeDetails";
import StorageLocationDetails from "./features/materials-storage-location/StorageLocationDetails";
import AssetTransfers from "./features/assets-transfers/AssetTransfers";
import MaterialTransfers from "./features/materials-transfers/MaterialTransfers";
import TechnicianRestocking from "./features/materials-restocking/TechnicianRestocking";
import MaterialsTransferHistory from "./features/materials-transfer-history/MaterialsTransferHistory";
import OfficeRestocking from "./features/materials-restocking/OfficeRestocking";
import { useAppDispatch, useAppSelector } from "./app/hooks";
import { updateNotifications } from "./features/nav/notificationsFunctions";
import MaterialsRMAList from "./features/materials-rma/MaterialsRMAList";
import MaterialsRMADetails from "./features/materials-rma/MaterialsRMADetails";
import MaterialsLossList from "./features/materials-losses/MaterialsLossList";
import MaterialsTechTransfers from "./features/materials-tech-transfer/MaterialsTechTransfer";
import MyInventory from "./features/materials-my-inventory/MyInventory";

import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MaterialAggregateList from "./features/materials-reports/MaterialAggregateList";

export const ThemeContext = React.createContext({
    theme: "",
    setTheme: (_theme: "dark" | "light") => {},
});

const App = () => {
    const [theme, setTheme] = useState<Nullable<string>>(localStorage.getItem("techwiseTheme"));
    const [useBorderlessNav, setUseBorderlessNav] = useState<boolean>(localStorage.getItem("navTheme") === "borderless");

    const contextValue = useMemo(() => ({ theme, setTheme }), [theme]);

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 1800000,
            },
        },
        queryCache: new QueryCache({
            onSuccess: (_data, _query) => {},
            onError: (error, _query) => {
                console.log(error);
            },
            onSettled: (_data, _error, _query) => {},
        }),
    });

    let idFromCache: Nullable<string> = localStorage.getItem("id");
    let tokenFromCache: Nullable<string> = localStorage.getItem("token");
    let usernameFromCache: Nullable<string> = localStorage.getItem("username");
    let firstNameFromCache: Nullable<string> = localStorage.getItem("firstName");
    let lastNameFromCache: Nullable<string> = localStorage.getItem("lastName");
    let emailFromCache: Nullable<string> = localStorage.getItem("email");

    let rolesFromCache: Nullable<string> = localStorage.getItem("roles");
    let isAuthenticated: boolean = useAppSelector(selectIsAuthenticated);

    let cachedCredentialsMiss: boolean =
        !idFromCache ||
        !tokenFromCache ||
        !usernameFromCache ||
        !firstNameFromCache ||
        !lastNameFromCache ||
        !emailFromCache ||
        !rolesFromCache;

    const rolesFromCacheProcessed: string[] = JSON.parse(rolesFromCache ?? "[]");
    const idFromCacheProcessed: number = parseInt(idFromCache ?? "-1");

    if (!cachedCredentialsMiss && !isAuthenticated) {
        dispatch(
            login({
                id: idFromCacheProcessed,
                username: usernameFromCache as string,
                last_name: lastNameFromCache as string,
                first_name: firstNameFromCache as string,
                token: tokenFromCache as string,
                roles: rolesFromCacheProcessed,
                email: emailFromCache as string,
            })
        );
    }

    let nav;

    let isAuth: boolean = useAppSelector(selectIsAuthenticated);
    let roles: string[] = useAppSelector(selectRoles);
    let token: string = useAppSelector(selectToken);

    function globalUpdates() {
        updateNotifications(dispatch);

        if (roles.includes("office") || roles.includes("technician")) {
            updateSites(dispatch, () => {});
            updateWorkOrders(dispatch, () => {});
            updateCompanies(dispatch, () => {});
        }

        if (roles.includes("office") || roles.includes("technician") || roles.includes("materials")) {
            // TODO determine if this is needed since queries do not persist.
            queryClient.invalidateQueries({ queryKey: ["OFFICE", "MATERIALS_DATABASE"] });
            updateTechwiseOffices(dispatch, () => {});
        }

        if (roles.includes("admin")) {
            updateAssets(dispatch, () => {});
            updateAssetsCatalog(dispatch, () => {});
            updateAvailableAssets(dispatch, () => {});
        }

        const sessionSocket: WebSocket = new WebSocket(
            `${process.env.REACT_APP_WS_ENDPOINT as string}/ws/session?token=${token}`
        );

        sessionSocket.onopen = (_event: Event) => console.log("WebSocket Connection established...");
        sessionSocket.onclose = (_event: Event) => console.log("WebSocket Connection closed...");
        sessionSocket.onerror = (_event: Event) => console.log("Error with WebSocket connection...");

        sessionSocket.onmessage = (event: any) => {
            // Malformed message, ignore it.
            if (!event.data) {
                return;
            }

            let data: { type: string; group: string } = JSON.parse(event.data);

            // Malformed message, ignore it.
            if (!data.type || !data.group) {
                return;
            }

            // Message is correct, but not the right type of message.
            if (data.type !== "pull_request") {
                return;
            }

            switch (data.group) {
                case "open_work_orders": {
                    if (roles.includes("office") || roles.includes("technician")) {
                        updateWorkOrders(dispatch, () => {});
                        queryClient.invalidateQueries({ queryKey: ["OFFICE", "WORK_ORDERS"] });
                    }
                    break;
                }
                case "sites": {
                    if (roles.includes("office") || roles.includes("technician")) {
                        updateSites(dispatch, () => {});
                    }
                    break;
                }
                case "companies": {
                    if (roles.includes("office") || roles.includes("technician")) {
                        updateCompanies(dispatch, () => {});
                    }
                    break;
                }
                case "materials_db": {
                    if (roles.includes("office") || roles.includes("technician") || roles.includes("materials")) {
                        queryClient.invalidateQueries({ queryKey: ["OFFICE", "MATERIALS_DATABASE"] });
                    }
                    break;
                }
                case "techwise_offices": {
                    if (roles.includes("office") || roles.includes("technician") || roles.includes("materials")) {
                        updateTechwiseOffices(dispatch, () => {});
                    }
                    break;
                }
                case "assets": {
                    updateAssets(dispatch, () => {});
                    updateAssetsCatalog(dispatch, () => {});
                    updateAvailableAssets(dispatch, () => {});
                    break;
                }
                case "notifications":
                    updateNotifications(dispatch);
                    break;
                default: {
                }
            }
        };
    }

    if (isAuth) {
        globalUpdates();
    }

    if (isAuth) {
        let routes = (
            <Routes>
                <Route path="/" element={<Home />} />

                <Route path="/help/change-password/:primary/:secondary" element={<ChangePassword />} />

                <Route
                    path="/work-orders"
                    element={
                        <ValidateRoles roles={["office"]}>
                            <WorkOrdersList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/assignments"
                    element={
                        <ValidateRoles roles={["office"]}>
                            <AssignmentsManager />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/database"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialsDatabase />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/work-orders/:workOrderId"
                    element={
                        <ValidateRoles roles={["office", "technician"]}>
                            <OfficeWorkOrder />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/work-sites"
                    element={
                        <ValidateRoles roles={["office"]}>
                            <WorkSitesList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/work-sites/:siteId"
                    element={
                        <ValidateRoles roles={["office", "technician"]}>
                            <WorkSiteDetails />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/companies"
                    element={
                        <ValidateRoles roles={["office"]}>
                            <CompaniesList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/companies/:companyId"
                    element={
                        <ValidateRoles roles={["office", "technician"]}>
                            <CompanyDetails />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/admin/users"
                    element={
                        <ValidateRoles roles={["admin"]}>
                            <UserManagement />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/admin/config"
                    element={
                        <ValidateRoles roles={["admin"]}>
                            <GlobalSettings />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/tech-dash"
                    element={
                        <ValidateRoles roles={["technician"]}>
                            <TechnicianDashboard />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/tech-dash/:entryId"
                    element={
                        <ValidateRoles roles={["technician"]}>
                            <TechnicianWorkOrder />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/assets/transfers"
                    element={
                        <ValidateRoles roles={["technician", "office", "materials", "projects", "admin"]}>
                            <AssetTransfers />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/tech-transfer"
                    element={
                        <ValidateRoles roles={["technician", "office", "materials", "projects", "admin"]}>
                            <MaterialsTechTransfers />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/my-inventory"
                    element={
                        <ValidateRoles roles={["technician", "office", "materials", "projects", "admin"]}>
                            <MyInventory />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/tech-dash/transfers"
                    element={
                        <ValidateRoles roles={["technician"]}>
                            <TechnicianDashboard />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/archive"
                    element={
                        <ValidateRoles roles={["office"]}>
                            <Archive />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/about"
                    element={
                        <ValidateRoles roles={["admin"]}>
                            <About />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/assets/manage"
                    element={
                        <ValidateRoles roles={["admin"]}>
                            <AssetsManager />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/assets/assign"
                    element={
                        <ValidateRoles roles={["admin"]}>
                            <AssetsAssignment />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/techwise-offices"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <TechwiseOfficesList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/techwise-office/:officeId"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <TechwiseOfficeDetails />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/locations/:storageLocationId"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <StorageLocationDetails />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/transfers"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialTransfers />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/restocking/office"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <OfficeRestocking />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/restocking/technician"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <TechnicianRestocking />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/history"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialsTransferHistory />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/rma"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialsRMAList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/rma/:rmaId"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialsRMADetails />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/losses"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialsLossList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/materials/reports"
                    element={
                        <ValidateRoles roles={["materials"]}>
                            <MaterialAggregateList />
                        </ValidateRoles>
                    }
                />

                <Route
                    path="/projects"
                    element={
                        <PageUnderConstruction
                            label="Projects"
                            breadcrumbs={
                                <StyledBreadcrumbs
                                    paths={[
                                        { text: "Techwise Portal", route: "/" },
                                        { text: "Projects", route: "/materials/reports" },
                                    ]}
                                />
                            }
                        />
                    }
                />
            </Routes>
        );

        if (useBorderlessNav) {
            nav = (
                <BorderlessNav forceRender={() => setUseBorderlessNav(localStorage.getItem("navTheme") === "borderless")}>
                    {routes}
                </BorderlessNav>
            );
        } else {
            nav = <Nav forceRender={() => setUseBorderlessNav(localStorage.getItem("navTheme") === "borderless")}>{routes}</Nav>;
        }
    } else {
        nav = (
            <StandaloneNav>
                <Routes>
                    <Route path="/*" element={<Login onLogin={loginHandler} />} />
                    <Route path="/help/change-password/:primary/:secondary" element={<ChangePassword />} />
                </Routes>
            </StandaloneNav>
        );
    }

    return (
        <StyledEngineProvider injectFirst>
            {/* @ts-ignore */}
            <ThemeContext.Provider value={contextValue}>
                <ThemeProvider theme={theme === "dark" ? techwiseThemeDark : techwiseThemeLight}>
                    {/*<InfoModal*/}
                    {/*    title="Connection Interrupted"*/}
                    {/*    description="Your connection to the Techwise Portal server has been interrupted. Please report this to the system administrator. Please refresh the page to continue."*/}
                    {/*    open={hasTimeout}*/}
                    {/*    handleClose={() => {}}*/}
                    {/*/>*/}
                    <QueryClientProvider client={queryClient}>
                        <BrowserRouter>{nav}</BrowserRouter>
                    </QueryClientProvider>
                </ThemeProvider>
            </ThemeContext.Provider>
        </StyledEngineProvider>
    );
};

export default App;
